import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const FileContext = createContext();

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024;
const WORKER_URL = 'https://storify-cors.storifyapp.site';

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const getFileIcon = (fileType = '') => {
  const type = fileType.toLowerCase();
  if (type.includes('image')) return 'image';
  if (type.includes('pdf')) return 'pdf';
  if (type.includes('word') || type.includes('document')) return 'word';
  if (type.includes('sheet') || type.includes('excel') || type.includes('csv')) return 'excel';
  if (type.includes('video')) return 'video';
  return 'file';
};

const enhanceFileData = (file) => ({
  ...file,
  icon: getFileIcon(file.type),
  formattedSize: formatFileSize(file.size),
});

export const FileProvider = ({ children }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshFiles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${WORKER_URL}/files`);
      if (!res.ok) throw new Error('Failed to fetch files');
      const json = await res.json();
      const enhanced = (json.files || []).map(enhanceFileData);
      setFiles(enhanced);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Failed to load files.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshFiles(); }, [refreshFiles]);

  const addFile = async ({ file, urlPath }) => {
    try {
      if (!file) throw new Error('Please select a file.');
      if (file.size > MAX_FILE_SIZE) throw new Error('File exceeds the 2GB limit.');

      const normalizedPath = urlPath.trim().startsWith('/') ? urlPath.trim() : `/${urlPath.trim()}`;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', normalizedPath);

      const res = await fetch(`${WORKER_URL}/files`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (res.status === 409) return { success: false, error: 'FILE_EXISTS', existingFile: { name: file.name } };
      if (res.status === 507) throw new Error('Storage limit reached.');
      if (!res.ok) throw new Error(data.error || 'Upload failed.');

      await refreshFiles();
      return { success: true, file: data.file };
    } catch (err) {
      console.error(err);
      return { success: false, error: err.message || 'Failed to save file.' };
    }
  };

  const replaceFile = async ({ file, urlPath }, existingFileId) => {
    try {
      if (existingFileId) await deleteFile(existingFileId);
      return await addFile({ file, urlPath });
    } catch (err) {
      console.error(err);
      return { success: false, error: err.message || 'Failed to replace file.' };
    }
  };

  const deleteFile = async (fileId) => {
    try {
      const res = await fetch(`${WORKER_URL}/files/${fileId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete file.');
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      return { success: true };
    } catch (err) {
      console.error(err);
      return { success: false, error: err.message || 'Failed to delete file.' };
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await fetch(`${WORKER_URL}/download/${file.id}`);
      if (!res.ok) throw new Error('File not available.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return { success: true };
    } catch (err) {
      console.error(err);
      return { success: false, error: err.message || 'Failed to download file.' };
    }
  };

  const clearAllFiles = async () => setFiles([]);

  const value = {
    files, setFiles, loading, error, configured: true,
    addFile, replaceFile, deleteFile, downloadFile,
    refreshFiles, clearAllFiles,
    maxFileSize: MAX_FILE_SIZE,
  };

  return <FileContext.Provider value={value}>{children}</FileContext.Provider>;
};

export const useFiles = () => {
  const context = useContext(FileContext);
  if (!context) throw new Error('useFiles must be used within a FileProvider');
  return context;
};

export default FileContext;